import { BillStatus } from "@redotech/crdb-enums/billing/bill-status";
import { BillType } from "@redotech/crdb-enums/billing/bill-type";
import { BillingChargeSubtype } from "@redotech/crdb-enums/billing/billing-charge-subtype";
import { isEnumValue } from "@redotech/util/enum";
import { pascalToSnakeCase } from "@redotech/util/string";
import { assertNever } from "@redotech/util/type";
import { SubscriptionProduct } from "./unified-billing/enums";

export enum BillingChargeType {
  Coverage = "Coverage",
  Return = "Return",
  ShippingLabel = "ShippingLabel",
  Subscription = "Subscription",
  Campaign = "Campaign",
  OrderTracking = "OrderTracking",
  CheckoutOptimization = "CheckoutOptimization",
  Recover = "Recover",
  Credit = "Credit",
  Usage = "Usage",
  Supplies = "Supplies",
}

export const chargeTypeLabels: Record<BillingChargeType, string> = {
  [BillingChargeType.Coverage]: "Coverage",
  [BillingChargeType.Return]: "Returns",
  [BillingChargeType.ShippingLabel]: "Shipping Labels",
  [BillingChargeType.Subscription]: "Subscription",
  [BillingChargeType.Campaign]: "Campaign",
  [BillingChargeType.OrderTracking]: "Order Tracking",
  [BillingChargeType.CheckoutOptimization]: "Checkout Optimization",
  [BillingChargeType.Recover]: "Recover",
  [BillingChargeType.Credit]: "Credit",
  [BillingChargeType.Usage]: "Usage",
  [BillingChargeType.Supplies]: "Supplies",
};

export function getChargeTypeLabel(
  chargeType: BillingChargeType | string,
): string {
  if (isEnumValue(chargeType, BillingChargeType)) {
    return chargeTypeLabels[chargeType as BillingChargeType];
  }
  return chargeType;
}
/**
 * Display-only subtypes that predate `BillingChargeSubtype` and still appear on
 * historical charges. Kept so old invoices do not regress to raw enum text.
 */
export type LegacyChargeSubtype =
  | "merchant_covered_label_damaged"
  | "merchant_covered_label_wrong_product";

/**
 * Every subtype a merchant can see, in the merchant's language. Typed against
 * the DB enum so a new subtype fails the build until someone names it.
 */
export const chargeSubtypeLabels: Record<
  BillingChargeSubtype | LegacyChargeSubtype,
  string
> = {
  [BillingChargeSubtype.CanceledPickupRefund]: "Canceled pickup refund",
  [BillingChargeSubtype.CanceledReturnLabelRefund]:
    "Canceled return label refund",
  [BillingChargeSubtype.CancelledExtendedWarrantyRefund]:
    "Extended warranty refund",
  [BillingChargeSubtype.CancelledFinalSaleReturnCoverageRefund]:
    "Final sale coverage refund",
  [BillingChargeSubtype.CancelledFinalSaleRevShareCoverageRefund]:
    "Final sale coverage revenue share refund",
  [BillingChargeSubtype.CancelledInstantExchangeRecovery]:
    "Instant exchange recovery reversal",
  [BillingChargeSubtype.CancelledOrderExtendedWarrantyRefund]:
    "Canceled-order extended warranty refund",
  [BillingChargeSubtype.CancelledOrderFinalSaleReturnCoverageRefund]:
    "Final sale coverage refund (canceled order)",
  [BillingChargeSubtype.CancelledOrderInstantExchangeRecovery]:
    "Canceled-order instant exchange recovery",
  [BillingChargeSubtype.CancelledOrderPackageProtectionPlusRefund]:
    "Package Protection+ refund (canceled order)",
  [BillingChargeSubtype.CancelledOrderRmrConsolidationCoverageShareRefund]:
    "Consolidation coverage share refund (canceled order)",
  [BillingChargeSubtype.CancelledOrderRmrProcessingCoverageShareRefund]:
    "Processing coverage share refund (canceled order)",
  [BillingChargeSubtype.CancelledPackageProtectionPlusRefund]:
    "Package Protection+ refund",
  [BillingChargeSubtype.CancelledPackageProtectionRefund]:
    "Package protection refund",
  [BillingChargeSubtype.CancelledPackageProtectionRevShareCoverageRefund]:
    "Package protection revenue share refund",
  [BillingChargeSubtype.CancelledPpPlusRevShareCoverageRefund]:
    "Package Protection+ revenue share refund",
  [BillingChargeSubtype.CancelledRevShareCoverageRefund]:
    "Coverage revenue share refund",
  [BillingChargeSubtype.CardProcessingFee]: "Card processing fee",
  [BillingChargeSubtype.CarrierClaimRefund]: "Carrier claim refund",
  [BillingChargeSubtype.CarrierPickup]: "Carrier pickup",
  [BillingChargeSubtype.CarrierUpgrade]: "Shopper-paid carrier upgrade",
  [BillingChargeSubtype.ChargeLabel]: "Shipping label",
  [BillingChargeSubtype.ChopAdjustment]: "Shipping adjustment",
  [BillingChargeSubtype.ClaimCommission]: "Claim commission",
  [BillingChargeSubtype.ClaimPayout]: "Claim payout",
  [BillingChargeSubtype.ClaimsAdjustment]: "Claims adjustment",
  [BillingChargeSubtype.ConsolidationShipping]: "Consolidation shipping",
  [BillingChargeSubtype.CoverageAdjustment]: "Coverage adjustment",
  [BillingChargeSubtype.CoverageRefundForCancelledOrder]:
    "Coverage refund (canceled order)",
  [BillingChargeSubtype.CustomerPaidLabel]: "Shopper-paid return label",
  [BillingChargeSubtype.Damaged]: "Damaged return",
  [BillingChargeSubtype.DeniedClaimPackageProtectionPlusRefund]:
    "Denied-claim Package Protection+ refund",
  [BillingChargeSubtype.DutyDrawbackCredit]: "Duty drawback credit",
  [BillingChargeSubtype.ExchangeShippingCredit]: "Exchange shipping credit",
  [BillingChargeSubtype.ExchangeOutboundLabelCredit]:
    "Exchange outbound label credit",
  [BillingChargeSubtype.ExtendedWarranty]: "Extended warranty",
  [BillingChargeSubtype.FinalSaleCredit]: "Final sale credit",
  [BillingChargeSubtype.FinalSaleCreditAdjustment]: "Dynamic FSR adjustment",
  [BillingChargeSubtype.FinalSaleReturnCoverage]: "Final sale coverage",
  [BillingChargeSubtype.FinalSaleRevShareCoverage]:
    "Final sale coverage revenue share",
  [BillingChargeSubtype.FreeTrialCredit]: "Free trial credit",
  [BillingChargeSubtype.FreightPurchase]: "Freight",
  [BillingChargeSubtype.FsrAdjustment]: "Final sale returns adjustment",
  [BillingChargeSubtype.ImplementationFee]: "Implementation fee",
  [BillingChargeSubtype.InstantExchangeRecovery]: "Instant exchange recovery",
  [BillingChargeSubtype.InstantRefund]: "Instant refund",
  [BillingChargeSubtype.Insurance]: "Shipping insurance",
  [BillingChargeSubtype.LandedCostDuties]: "Duties",
  [BillingChargeSubtype.LandedCostFees]: "Landed cost fees",
  [BillingChargeSubtype.LandedCostTaxes]: "Import taxes",
  [BillingChargeSubtype.ManagedClaimCredit]: "Managed claim credit",
  [BillingChargeSubtype.ManualAdjustment]: "Manual adjustment",
  [BillingChargeSubtype.ManualRefund]: "Manual refund",
  [BillingChargeSubtype.MarketingAdjustment]: "Marketing adjustment",
  [BillingChargeSubtype.MerchantCarrierFee]: "Carrier fee",
  [BillingChargeSubtype.MerchantCarrierLabelCredit]: "Carrier label credit",
  [BillingChargeSubtype.MerchantCoveredLabel]: "Merchant covered label",
  [BillingChargeSubtype.MerchantCreatedReturnLabel]:
    "Merchant-created return label",
  [BillingChargeSubtype.MerchantIncentive]: "Merchant incentive",
  [BillingChargeSubtype.MerchantPaidPpPlusPerOrder]:
    "Package Protection+ (merchant paid)",
  [BillingChargeSubtype.Miscellaneous]: "Miscellaneous",
  [BillingChargeSubtype.NonCoverageAdjustment]: "Non-coverage adjustment",
  [BillingChargeSubtype.OmsAdjustment]: "Shipping adjustment",
  [BillingChargeSubtype.OrderTracking]: "Order tracking",
  [BillingChargeSubtype.OrderTrackingAdjustment]: "Order tracking adjustment",
  [BillingChargeSubtype.OtherAdjustment]: "Other adjustment",
  [BillingChargeSubtype.OutboundLabel]: "Outbound label",
  [BillingChargeSubtype.PackagePickup]: "Package pickup",
  [BillingChargeSubtype.PackageProtection]: "Package protection",
  [BillingChargeSubtype.PackageProtectionPlus]: "Package Protection+",
  [BillingChargeSubtype.PackageProtectionRevShareCoverage]:
    "Package protection revenue share",
  [BillingChargeSubtype.PerEmail]: "Per email",
  [BillingChargeSubtype.PerExchangePayoutFee]: "Per-exchange payout fee",
  [BillingChargeSubtype.PerOrder]: "Per order",
  [BillingChargeSubtype.PerRecycleRequestFee]: "Per-recycle-request fee",
  [BillingChargeSubtype.PerReturnFee]: "Per-return fee",
  [BillingChargeSubtype.PerSms]: "Per SMS",
  [BillingChargeSubtype.PersonalizedInsertFee]: "Personalized insert fee",
  [BillingChargeSubtype.PersonalizedInsertRevenueShare]:
    "Personalized insert revenue share",
  [BillingChargeSubtype.PointOfSale]: "Point of sale",
  [BillingChargeSubtype.PpPlusAdjustment]: "Package Protection+ adjustment",
  [BillingChargeSubtype.PpPlusRevShareCoverage]:
    "Package Protection+ revenue share",
  [BillingChargeSubtype.PromotionalCredit]: "Promotional credit",
  [BillingChargeSubtype.PurchaseOrderItem]: "Item",
  [BillingChargeSubtype.PurchaseOrderShipping]: "Shipping",
  [BillingChargeSubtype.ReFulfillmentFixedFee]: "Refulfillment flat fee",
  [BillingChargeSubtype.ReFulfillmentRevenueShare]:
    "Refulfillment percentage fee",
  [BillingChargeSubtype.ReFulfillmentShippingAdjustment]:
    "Refulfillment shipping cost",
  [BillingChargeSubtype.RedeemPoints]: "Points redeemed",
  [BillingChargeSubtype.RedoManagedReturnsCoverage]: "Managed returns coverage",
  [BillingChargeSubtype.RedoSupply]: "Redo supplies",
  [BillingChargeSubtype.ReduceBalance]: "Balance reduction",
  [BillingChargeSubtype.Refund]: "Refund",
  [BillingChargeSubtype.RepairFeeCredit]: "Repair fee credit",
  [BillingChargeSubtype.ReturnCoverage]: "Return coverage",
  [BillingChargeSubtype.ReturnFraudPrevention]: "Return fraud prevention",
  [BillingChargeSubtype.ReturnLabel]: "Return label",
  [BillingChargeSubtype.ReturnLabelAdjustment]: "Return label adjustment",
  [BillingChargeSubtype.ReturnLabelMarkupRevenue]: "Return label markup",
  [BillingChargeSubtype.ReturnsProcessingFee]: "Returns processing fee",
  [BillingChargeSubtype.ExternalReturnsProcessingFee]:
    "External returns processing fee",
  [BillingChargeSubtype.RevShareCoverage]: "Coverage revenue share",
  [BillingChargeSubtype.RmrAdjustment]: "Managed returns adjustment",
  [BillingChargeSubtype.RmrConsolidationCoverageShare]:
    "Consolidation coverage share",
  [BillingChargeSubtype.RmrProcessingCoverageShare]:
    "Processing coverage share",
  [BillingChargeSubtype.SalesTax]: "Sales tax",
  [BillingChargeSubtype.ShippingCostShare]: "Shipping cost share",
  [BillingChargeSubtype.ShopperAiAdjustment]: "Shopper AI adjustment",
  [BillingChargeSubtype.SubscriptionAdjustment]: "Subscription adjustment",
  [BillingChargeSubtype.SubscriptionFee]: "Subscription fee",
  [BillingChargeSubtype.Surcharge]: "Surcharge",
  [BillingChargeSubtype.TransactionFailureClawback]: "Failed payment reversal",
  [BillingChargeSubtype.UpsellRevenue]: "Exchange upsell",
  [BillingChargeSubtype.UpsellRevenueRefunded]: "Upsell revenue refund",
  [BillingChargeSubtype.UspsPcPostageFee]: "USPS postage fee",
  [BillingChargeSubtype.VoidFreightPurchase]: "Freight void",
  [BillingChargeSubtype.VoidInsurance]: "Shipping insurance void",
  [BillingChargeSubtype.VoidLabel]: "Label void",
  [BillingChargeSubtype.VoidLandedCostDuties]: "Duties refund",
  [BillingChargeSubtype.VoidLandedCostFees]: "Landed cost fees refund",
  [BillingChargeSubtype.VoidLandedCostTaxes]: "Import taxes refund",
  [BillingChargeSubtype.VoidUsedLabel]: "Used label void",
  [BillingChargeSubtype.WarrantiesAdjustment]: "Warranties adjustment",
  [BillingChargeSubtype.WmsFee]: "Warehouse fee",
  [BillingChargeSubtype.Writeoff]: "Write-off",
  [BillingChargeSubtype.WrongProduct]: "Wrong product return",
  [BillingChargeSubtype.ZeroOutAccountBalance]: "Balance zeroed out",
  merchant_covered_label_damaged: "Damaged label",
  merchant_covered_label_wrong_product: "Wrong product label",
};

/**
 * Charge subtypes reach the UI in two dialects: snake_case DB values and legacy
 * PascalCase names. Normalize before lookup so both find the same label.
 */
export function getChargeSubtypeLabel(subtype: string): string {
  return (
    chargeSubtypeLabels[
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      pascalToSnakeCase(subtype) as BillingChargeSubtype | LegacyChargeSubtype
    ] ?? subtype
  );
}

export const billTypeLabels: Record<BillType, string> = {
  [BillType.Shipping]: "Shipping",
  [BillType.ReturnShipping]: "Returns",
  [BillType.Software]: "Software",
  [BillType.PurchaseOrder]: "Purchase Order",
};

export function getBillTypeLabel(billType: BillType | string): string {
  if (isEnumValue(billType, BillType)) {
    return billTypeLabels[billType as BillType];
  }
  return billType;
}

export const billStatusLabels: Record<BillStatus, string> = {
  [BillStatus.Open]: "Upcoming",
  [BillStatus.Closed]: "Pending",
  [BillStatus.Paid]: "Paid",
  [BillStatus.Void]: "Void",
  [BillStatus.Failed]: "Failed",
  [BillStatus.Refunded]: "Refunded",
  [BillStatus.PartiallyRefunded]: "Partially Refunded",
};

export type ChargeTypeBadgeColor =
  | "blueLight"
  | "blue"
  | "indigo"
  | "purple"
  | "pink"
  | "orange"
  | "brand"
  | "grayBlue"
  | "success"
  | "gray";

export const chargeTypeColors: Record<BillingChargeType, ChargeTypeBadgeColor> =
  {
    [BillingChargeType.Coverage]: "blueLight",
    [BillingChargeType.Return]: "blue",
    [BillingChargeType.ShippingLabel]: "indigo",
    [BillingChargeType.Subscription]: "purple",
    [BillingChargeType.Campaign]: "pink",
    [BillingChargeType.OrderTracking]: "orange",
    [BillingChargeType.CheckoutOptimization]: "brand",
    [BillingChargeType.Recover]: "grayBlue",
    [BillingChargeType.Credit]: "success",
    [BillingChargeType.Usage]: "orange",
    [BillingChargeType.Supplies]: "gray",
  };

export function getChargeTypeColor(
  chargeType: BillingChargeType | string,
): ChargeTypeBadgeColor {
  if (isEnumValue(chargeType, BillingChargeType)) {
    return chargeTypeColors[chargeType as BillingChargeType];
  }
  return "gray";
}

// Subtypes are grouped by their parent charge type for consistent color coding.
const subtypeToChargeType: Partial<
  Record<BillingChargeSubtype | LegacyChargeSubtype, BillingChargeType>
> = {
  [BillingChargeSubtype.ReturnCoverage]: BillingChargeType.Coverage,
  [BillingChargeSubtype.PackageProtectionPlus]: BillingChargeType.Coverage,
  [BillingChargeSubtype.PackageProtection]: BillingChargeType.Coverage,
  [BillingChargeSubtype.FinalSaleReturnCoverage]: BillingChargeType.Coverage,
  [BillingChargeSubtype.ExtendedWarranty]: BillingChargeType.Coverage,
  [BillingChargeSubtype.UpsellRevenue]: BillingChargeType.Coverage,
  [BillingChargeSubtype.FinalSaleCreditAdjustment]: BillingChargeType.Coverage,
  [BillingChargeSubtype.RevShareCoverage]: BillingChargeType.Coverage,
  [BillingChargeSubtype.FinalSaleRevShareCoverage]: BillingChargeType.Coverage,
  [BillingChargeSubtype.PpPlusRevShareCoverage]: BillingChargeType.Coverage,
  [BillingChargeSubtype.PackageProtectionRevShareCoverage]:
    BillingChargeType.Coverage,
  [BillingChargeSubtype.ManagedClaimCredit]: BillingChargeType.Return,
  [BillingChargeSubtype.PerOrder]: BillingChargeType.Return,
  [BillingChargeSubtype.PerReturnFee]: BillingChargeType.Return,
  [BillingChargeSubtype.ReturnsProcessingFee]: BillingChargeType.Return,
  [BillingChargeSubtype.ExternalReturnsProcessingFee]: BillingChargeType.Return,
  [BillingChargeSubtype.DutyDrawbackCredit]: BillingChargeType.Return,
  [BillingChargeSubtype.MerchantCoveredLabel]: BillingChargeType.ShippingLabel,
  [BillingChargeSubtype.MerchantCarrierLabelCredit]:
    BillingChargeType.ShippingLabel,
  [BillingChargeSubtype.ShippingCostShare]: BillingChargeType.ShippingLabel,
  [BillingChargeSubtype.ReturnLabelMarkupRevenue]:
    BillingChargeType.ShippingLabel,
  [BillingChargeSubtype.CustomerPaidLabel]: BillingChargeType.ShippingLabel,
  [BillingChargeSubtype.CarrierUpgrade]: BillingChargeType.ShippingLabel,
  [BillingChargeSubtype.PerSms]: BillingChargeType.Campaign,
  [BillingChargeSubtype.PerEmail]: BillingChargeType.Campaign,
  [BillingChargeSubtype.PurchaseOrderItem]: BillingChargeType.Supplies,
  [BillingChargeSubtype.PurchaseOrderShipping]: BillingChargeType.Supplies,
  [BillingChargeSubtype.SalesTax]: BillingChargeType.Supplies,
  [BillingChargeSubtype.ExchangeOutboundLabelCredit]:
    BillingChargeType.ShippingLabel,
  merchant_covered_label_damaged: BillingChargeType.ShippingLabel,
  merchant_covered_label_wrong_product: BillingChargeType.ShippingLabel,
};

export function getChargeSubtypeColor(subtype: string): ChargeTypeBadgeColor {
  const parentType =
    subtypeToChargeType[
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      pascalToSnakeCase(subtype) as BillingChargeSubtype | LegacyChargeSubtype
    ];
  return parentType ? chargeTypeColors[parentType] : "gray";
}

export function getBillStatusLabel(status: BillStatus | string): string {
  if (isEnumValue(status, BillStatus)) {
    return billStatusLabels[status as BillStatus];
  }
  return status;
}

export type BillStatusTheme = "success" | "warning" | "error" | "default";

export const billStatusThemes: Record<BillStatus, BillStatusTheme> = {
  [BillStatus.Open]: "warning",
  [BillStatus.Closed]: "warning",
  [BillStatus.Paid]: "success",
  [BillStatus.Void]: "error",
  [BillStatus.Failed]: "error",
  [BillStatus.Refunded]: "success",
  [BillStatus.PartiallyRefunded]: "success",
};

export function getBillStatusTheme(
  status: BillStatus | string,
): BillStatusTheme {
  if (isEnumValue(status, BillStatus)) {
    return billStatusThemes[status as BillStatus];
  }
  return "default";
}

/**
 * The status a merchant actually sees, derived from the persisted `BillStatus`
 * plus whether the bill has any billing transaction. The persisted status
 * cannot distinguish a closed bill that is awaiting its first (manual)
 * collection from one whose charge is in flight — both sit at `Closed` — so the
 * display layer splits them: a closed bill with no transaction is `New`
 * (awaiting payment, payable now), one with a transaction is `Pending`
 * (in-flight, e.g. an ACH charge settling). This is the single source of truth
 * for both the unified invoices UI and the legacy payouts status
 * (`payoutStatusFromBill`).
 */
export enum BillDisplayStatus {
  New = "New",
  Open = "Open",
  Pending = "Pending",
  Paid = "Paid",
  Void = "Void",
  Failed = "Failed",
  Refunded = "Refunded",
  PartiallyRefunded = "PartiallyRefunded",
  // A closed period that netted a credit (merchant owed): nothing was charged
  // and the negative balance carries to the next period. Distinct from `New`
  // (which is a closed-but-unpaid amount OWED, awaiting payment).
  Credit = "Credit",
  // A closed, unpaid invoice on payment terms: owed, dated, and payable now,
  // but not late — it is collected on its due date. Distinct from `Deferred`,
  // which is a charge we could not make yet, and from `New`, which is awaiting
  // a first manual charge.
  Due = "Due",
  // Closed on cadence, charge intentionally held (new/absent card); the sweep
  // collects it automatically once the card is chargeable.
  Deferred = "Deferred",
}

export function deriveBillDisplayStatus(
  status: BillStatus,
  hasTransactions: boolean,
  // The bill's net balance. A closed bill with no real transaction and a
  // negative balance is a CREDIT (carried forward), not an amount-owed `New`.
  balanceEnd?: number,
  // Merchant-display only: a deferred closed bill reads `Deferred`, not `New`.
  // Omitted by `payoutStatusFromBill` so the sweep's `new` guard still fires.
  deferred?: boolean,
  // Why it was deferred. `payment_terms` is an invoice the merchant can pay from
  // the Bills page today and that is otherwise collected when due; every other
  // reason means we are waiting on a payment method, and Pay would be wrong.
  deferredReason?: string | null,
): BillDisplayStatus {
  switch (status) {
    case BillStatus.Open:
      return BillDisplayStatus.Open;
    case BillStatus.Closed:
      if (hasTransactions) {
        return BillDisplayStatus.Pending;
      }
      if (balanceEnd !== undefined && balanceEnd < 0) {
        return BillDisplayStatus.Credit;
      }
      // Closed at zero owes nothing and never will, so `New` would invite a $0
      // payment. Deferring a charge that does not exist is meaningless too.
      if (balanceEnd === 0) {
        return BillDisplayStatus.Paid;
      }
      if (deferred && deferredReason === "payment_terms") {
        return BillDisplayStatus.Due;
      }
      return deferred ? BillDisplayStatus.Deferred : BillDisplayStatus.New;
    case BillStatus.Paid:
      return BillDisplayStatus.Paid;
    case BillStatus.Void:
      return BillDisplayStatus.Void;
    case BillStatus.Failed:
      return BillDisplayStatus.Failed;
    case BillStatus.Refunded:
      return BillDisplayStatus.Refunded;
    case BillStatus.PartiallyRefunded:
      return BillDisplayStatus.PartiallyRefunded;
    default:
      return assertNever(status);
  }
}

export const billDisplayStatusLabels: Record<BillDisplayStatus, string> = {
  [BillDisplayStatus.New]: "New",
  // Still accruing — merchant-facing wording for a period that hasn't closed.
  [BillDisplayStatus.Open]: "Upcoming",
  [BillDisplayStatus.Pending]: "Pending",
  [BillDisplayStatus.Paid]: "Paid",
  [BillDisplayStatus.Void]: "Void",
  [BillDisplayStatus.Failed]: "Failed",
  [BillDisplayStatus.Refunded]: "Refunded",
  [BillDisplayStatus.PartiallyRefunded]: "Partially Refunded",
  [BillDisplayStatus.Credit]: "Credit",
  [BillDisplayStatus.Deferred]: "Scheduled",
  [BillDisplayStatus.Due]: "Due",
};

export const billDisplayStatusThemes: Record<
  BillDisplayStatus,
  BillStatusTheme
> = {
  [BillDisplayStatus.New]: "warning",
  [BillDisplayStatus.Open]: "warning",
  [BillDisplayStatus.Pending]: "warning",
  [BillDisplayStatus.Paid]: "success",
  [BillDisplayStatus.Void]: "error",
  [BillDisplayStatus.Failed]: "error",
  [BillDisplayStatus.Refunded]: "success",
  [BillDisplayStatus.PartiallyRefunded]: "success",
  [BillDisplayStatus.Credit]: "success",
  [BillDisplayStatus.Deferred]: "default",
  [BillDisplayStatus.Due]: "warning",
};

export enum TransactionStatus {
  Pending = "Pending",
  Success = "Success",
  Failed = "Failed",
}

export const transactionStatusLabels: Record<TransactionStatus, string> = {
  [TransactionStatus.Pending]: "Pending Payment",
  [TransactionStatus.Success]: "Payment",
  [TransactionStatus.Failed]: "Failed Payment",
};

export function getTransactionStatusLabel(
  status: TransactionStatus | string,
): string {
  if (isEnumValue(status, TransactionStatus)) {
    return transactionStatusLabels[status as TransactionStatus];
  }
  return status;
}

export enum ShopifySubscriptionStatus {
  ACTIVE = "ACTIVE",
  CANCELLED = "CANCELLED",
  DECLINED = "DECLINED",
  EXPIRED = "EXPIRED",
  FROZEN = "FROZEN",
  PENDING = "PENDING",
  ACCEPTED = "ACCEPTED",
}

export const shopifySubscriptionStatusLabels: Record<
  ShopifySubscriptionStatus,
  string
> = {
  [ShopifySubscriptionStatus.ACTIVE]: "Active",
  [ShopifySubscriptionStatus.CANCELLED]: "Cancelled",
  [ShopifySubscriptionStatus.DECLINED]: "Declined",
  [ShopifySubscriptionStatus.EXPIRED]: "Expired",
  [ShopifySubscriptionStatus.FROZEN]: "Frozen",
  [ShopifySubscriptionStatus.PENDING]: "Pending",
  [ShopifySubscriptionStatus.ACCEPTED]: "Accepted",
};

export function getShopifySubscriptionStatusLabel(
  status: ShopifySubscriptionStatus | string,
): string {
  if (isEnumValue(status, ShopifySubscriptionStatus)) {
    return shopifySubscriptionStatusLabels[status as ShopifySubscriptionStatus];
  }
  return status;
}

export type ShopifySubscriptionStatusColor =
  | "success"
  | "warning"
  | "error"
  | "gray";

export const shopifySubscriptionStatusColors: Record<
  ShopifySubscriptionStatus,
  ShopifySubscriptionStatusColor
> = {
  [ShopifySubscriptionStatus.ACTIVE]: "success",
  [ShopifySubscriptionStatus.CANCELLED]: "error",
  [ShopifySubscriptionStatus.DECLINED]: "error",
  [ShopifySubscriptionStatus.EXPIRED]: "gray",
  [ShopifySubscriptionStatus.FROZEN]: "warning",
  [ShopifySubscriptionStatus.PENDING]: "warning",
  [ShopifySubscriptionStatus.ACCEPTED]: "success",
};

export function getShopifySubscriptionStatusColor(
  status: ShopifySubscriptionStatus | string,
): ShopifySubscriptionStatusColor {
  if (isEnumValue(status, ShopifySubscriptionStatus)) {
    return shopifySubscriptionStatusColors[status as ShopifySubscriptionStatus];
  }
  return "gray";
}

export const subscriptionProductLabels: Record<SubscriptionProduct, string> = {
  [SubscriptionProduct.MarketingSms]: "Marketing SMS",
  [SubscriptionProduct.MarketingEmail]: "Marketing Email",
  [SubscriptionProduct.OrderTracking]: "Order Tracking",
  [SubscriptionProduct.ReturnAiExchange]: "AI Exchange",
  [SubscriptionProduct.Returns]: "Returns",
  [SubscriptionProduct.CheckoutOptimization]: "Checkout Optimization",
  [SubscriptionProduct.Aeo]: "AEO",
  [SubscriptionProduct.Ims]: "IMS",
  [SubscriptionProduct.Recover]: "Recover",
  [SubscriptionProduct.SupportBase]: "Support",
  [SubscriptionProduct.SupportSms]: "Support SMS",
  [SubscriptionProduct.Oms]: "OMS",
  [SubscriptionProduct.Wms]: "WMS",
  [SubscriptionProduct.Disputes]: "Disputes",
  [SubscriptionProduct.Lcg]: "LCG",
  [SubscriptionProduct.Ctx]: "Contextual",
  [SubscriptionProduct.DimensionalPackaging]: "Dimensional Packaging",
  [SubscriptionProduct.Supplies]: "Supplies",
};

export function getSubscriptionProductLabel(
  product: SubscriptionProduct | string,
): string {
  if (isEnumValue(product, SubscriptionProduct)) {
    return subscriptionProductLabels[product as SubscriptionProduct];
  }
  return product;
}

export type SubscriptionProductBadgeColor = ChargeTypeBadgeColor;

export const subscriptionProductColors: Record<
  SubscriptionProduct,
  SubscriptionProductBadgeColor
> = {
  [SubscriptionProduct.MarketingSms]: "pink",
  [SubscriptionProduct.MarketingEmail]: "pink",
  [SubscriptionProduct.OrderTracking]: "orange",
  [SubscriptionProduct.ReturnAiExchange]: "blue",
  [SubscriptionProduct.Returns]: "blue",
  [SubscriptionProduct.CheckoutOptimization]: "orange",
  [SubscriptionProduct.Aeo]: "brand",
  [SubscriptionProduct.Ims]: "indigo",
  [SubscriptionProduct.Recover]: "grayBlue",
  [SubscriptionProduct.SupportBase]: "purple",
  [SubscriptionProduct.SupportSms]: "purple",
  [SubscriptionProduct.Oms]: "blueLight",
  [SubscriptionProduct.Wms]: "blueLight",
  [SubscriptionProduct.Disputes]: "success",
  [SubscriptionProduct.Lcg]: "blueLight",
  [SubscriptionProduct.Ctx]: "brand",
  [SubscriptionProduct.DimensionalPackaging]: "orange",
  [SubscriptionProduct.Supplies]: "blueLight",
};

export function getSubscriptionProductColor(
  product: SubscriptionProduct | string,
): SubscriptionProductBadgeColor {
  if (isEnumValue(product, SubscriptionProduct)) {
    return subscriptionProductColors[product as SubscriptionProduct];
  }
  return "gray";
}

export enum PaymentProvider {
  Stripe = "Stripe",
  Shopify = "Shopify",
}

export enum PaymentMethodType {
  Card = "Card",
  UsBankAccount = "UsBankAccount",
  ShopifyAppSubscription = "ShopifyAppSubscription",
}

export enum PaymentMethodPriority {
  Primary = "Primary",
  Secondary = "Secondary",
  ShopifySubscription = "ShopifySubscription",
  Deactivated = "Deactivated",
}
